import { useEffect, useRef, useState } from 'react'
import { hashKey, useQueryClient } from '@tanstack/react-query'
import {
  useCleanupKeywords,
  useExecuteCleanup,
  useResolveCleanupReconcile,
  cleanupKeywordsQueryKey,
  describeCleanupRun,
  readCleanupReconcileBody,
  type CleanupExecuteResult,
  type CleanupOpenReconcile,
  type CleanupReconcileOutcome,
  type CleanupRunOutcome,
} from '@/api/promotions'
import { ApiError, reconcileRequiredCount } from '@/api/client'
import { IS_DEMO_MODE } from '@/lib/demoMode'
import { Trash2, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react'

export default function CleanupView() {
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [offset, setOffset] = useState(0)
  const limit = 50

  // Die Abfrage und ihr Schluessel als EIN Wert (Whole-Branch-Fix C2): die
  // Blockade unten muss sagen koennen, auf WELCHEN Abruf sie wartet.
  const abfrage = { daysThreshold: 30, limit, offset }
  const markerSchluessel = hashKey(cleanupKeywordsQueryKey(abfrage))
  const queryClient = useQueryClient()

  const { data, isLoading, isError, dataUpdatedAt } = useCleanupKeywords(abfrage)
  const executeCleanup = useExecuteCleanup()
  const resolveReconcile = useResolveCleanupReconcile()

  // CLEANUP-SICHT: Zahl der Eintraege des LETZTEN LAUFS mit ungeklaertem
  // Amazon-Zustand (HTTP 502 / RECONCILE_REQUIRED). 0 = kein Hinweis.
  const [reconcileCount, setReconcileCount] = useState(0)
  // Die BEKANNTEN Ausgaenge des letzten Laufs — bestaetigte Erfolge und
  // deterministische Fehlschlaege. Gefuellt aus BEIDEN Antwortformen: aus dem
  // 200-Rumpf und (Fix-Runde 1, Finding 1) aus dem `result` des 502.
  const [runOutcome, setRunOutcome] = useState<CleanupRunOutcome | null>(null)
  // Fix-Runde 2: Warnung, wenn die Rueckmeldung des Laufs sich widersprochen hat.
  const [runWarnung, setRunWarnung] = useState<string | null>(null)
  // Fix-Runde 3: Die Reconcile-Zahl stammt aus einer als unzuverlaessig
  // erkannten Antwort und darf dann nicht als exakter Wert dastehen.
  const [reconcileUnsicher, setReconcileUnsicher] = useState(false)
  // DIE SPERRMENGE — Keywords, deren Amazon-Zustand in dieser Sitzung als
  // ungeklaert BEOBACHTET wurde. Sie waechst MONOTON (Fix-Runde 3): jede
  // gesehene Marker-Antwort und jeder Lauf vereinigen sich hinein, entfernt wird
  // nur durch ein bestaetigtes Ereignis (Quittierung — Erfolg oder 404).
  //
  // Weder ein Refetch noch ein Seitenwechsel noch ein Fehler darf sie schrumpfen
  // lassen. Waehrend eines Seitenwechsels faellt `data` auf `undefined` und
  // `openReconciles` damit auf `[]` — die Quelle sieht dann LEER aus, ohne leer
  // zu SEIN. Wer daraus „nichts ist gesperrt" liest, laesst in genau diesem
  // Fenster eine gesperrte ID wieder in den POST.
  const [gesperrteIds, setGesperrteIds] = useState<Set<number>>(new Set())
  // DIE ERLEDIGT-MENGE (Fix-Runde 4) — Keywords, die in dieser Sitzung
  // BESTAETIGT bei Amazon archiviert wurden. Bewusst NICHT mit `gesperrteIds`
  // zusammengelegt: dort steht ein ungeklaerter Ausgang, der sich durch eine
  // Quittierung aufloest; hier steht ein bekannter Erfolg, der sich gar nicht
  // mehr aufloest. Verschiedene Ursachen, verschiedene Auswege, verschiedene
  // Saetze fuer den Operator.
  //
  // Auch sie waechst MONOTON, und zwar aus demselben Grund: die Kandidatenliste
  // hinkt einem Lauf nach (laufender Refetch, Report-Lag), ein gerade
  // archiviertes Keyword steht dort also noch eine Weile — sichtbar und formal
  // anwaehlbar. Wer die Menge an die Liste koppelt, laesst „alle auswaehlen"
  // genau dieses Keyword erneut aufnehmen und dispatcht es ein zweites Mal.
  const [erledigteIds, setErledigteIds] = useState<Set<number>>(new Set())
  // Fix-Runde 5: Der EINE Zustand, in dem die Freigabe des Hintergrund-Refetchs
  // ins Leere greift. Nach einer widerspruechlichen Rueckmeldung ist nicht
  // entscheidbar, welches Keyword welchen Ausgang hatte — und anders als beim
  // ungeklaerten Ausgang gibt es keine Menge, in die es wandern koennte:
  // sperren ginge nicht (es gaebe keinen Marker zum Quittieren), erledigt waere
  // gelogen. Der Batch wird deshalb nur abgewaehlt — und waere ohne diese
  // Klammer sofort wieder anwaehlbar und absendbar. Die Blockade gilt NUR fuer
  // diesen Zustand und endet mit dem naechsten geglueckten Marker-Abruf; ab da
  // tragen die Marker die Wahrheit.
  //
  // WHOLE-BRANCH-FIX C2 — die Blockade merkt sich jetzt, WORAUF sie wartet.
  // Bisher hing sie an einem blossen Wechsel von `dataUpdatedAt`. Der tritt aber
  // auch ein, wenn ein ANDERER Query-Schluessel beobachtet wird: ein
  // Seitenwechsel wechselt den Schluessel und damit den Wert, ohne dass die
  // Marker der betroffenen Seite je neu geladen worden waeren. Die Blockade fiel
  // dabei lautlos, und zurueckgeblaettert war derselbe Batch wieder absendbar.
  // Also: Schluessel UND Stand des Abrufs festhalten, an dem der Widerspruch
  // auftrat, und nur ein NEUERER erfolgreicher Abruf GENAU DIESES Schluessels
  // gibt frei. Jeder andere Schluessel laesst die Blockade stehen — fail-closed.
  const [markerBlockade, setMarkerBlockade] = useState<{ schluessel: string; seit: number } | null>(null)
  // Synchroner Doppelklick-Schutz: `executeCleanup.isPending` wird erst mit dem
  // naechsten Render sichtbar. Zwei Klicks im selben Durchlauf kaemen beide
  // durch und dispatchten denselben Batch zweimal.
  const absendenLaeuft = useRef(false)
  // Rueckmeldungen der Quittierung. BEWUSST kein Toast und bewusst AUSSERHALB
  // des Marker-Abschnitts gerendert: quittiert der Operator den letzten offenen
  // Marker, verschwindet der Abschnitt — die Rueckmeldung darf nicht mit ihm
  // verschwinden. Mehrzahl seit Whole-Branch-Fix I1: eine Quittierung kann
  // ZWEI verschiedene Dinge zu melden haben (lokale Buchung ohne Wirkung und
  // ausgefallene Audit-Spur), und keine der beiden darf die andere verdraengen.
  const [resolveHinweise, setResolveHinweise] = useState<string[]>([])

  const kws = data?.keywords ?? []
  const total = data?.total ?? 0
  // DER ANGEZEIGTE AUSSCHNITT — die neuesten Marker mit Text, Grund und
  // Zeitpunkt. NICHT der Bestand (Whole-Branch-Fix C3).
  const openReconciles = data?.openReconciles ?? []
  // DER BESTAND und die vollstaendige SPERRQUELLE. Der Ausschnitt kann beides
  // nicht liefern: er endet bei 100 Zeilen, und bis hierhin galt genau diese
  // Laenge als „alles, was offen ist". Ab Marker 101 blieb ein aelterer
  // betroffener Fall nach einem Reload ungesperrt und erneut dispatchbar.
  const offeneMarkerGesamt = data?.openReconcilesTotal ?? openReconciles.length
  const alleOffenenMarkerIds = data?.openReconcileKeywordIds ?? []
  const page = Math.floor(offset / limit)
  const totalPages = Math.ceil(total / limit)

  // Jede beobachtete Marker-Antwort wandert in die Sperrmenge — und zwar die
  // VOLLSTAENDIGE ID-Liste, nicht der Ausschnitt. Der Effekt laeuft NACH dem
  // Render, deshalb unten zusaetzlich die Sofort-Vereinigung.
  useEffect(() => {
    if (!data) return
    const beobachtet = [
      ...(data.openReconcileKeywordIds ?? []),
      // Der Ausschnitt ist eine Teilmenge davon — mitgenommen, damit die Sperre
      // auch dann traegt, wenn die ID-Liste einmal fehlen sollte.
      ...data.openReconciles.map(r => r.keywordId),
    ]
    if (beobachtet.length === 0) return
    setGesperrteIds(prev => {
      const neue = beobachtet.filter(id => !prev.has(id))
      // Unveraenderte Referenz zurueckgeben, sonst rendert der Effekt sich selbst
      // in eine Schleife.
      if (neue.length === 0) return prev
      return new Set([...prev, ...neue])
    })
  }, [data])

  // Der Ausstieg aus der Blockade von oben: ein GEGLUECKTER Marker-Abruf GENAU
  // DIESES Schluessels. `dataUpdatedAt` rueckt nur bei einem erfolgreichen
  // Abruf vor — ein fehlgeschlagener haelt die Blockade, dort greift ohnehin
  // schon `isError`. Bewusst nicht an `isFetching` gehaengt: das wuerde
  // voraussetzen, dass zum Zeitpunkt des Fehlschlags bereits ein Refetch laeuft,
  // und liesse die Blockade lautlos aus, wenn sich diese Reihenfolge einmal
  // verschiebt. Und bewusst mit Schluesselvergleich (C2): ein Wert, der von
  // einer anderen Seite stammt, sagt ueber diese hier nichts.
  const blockadeAufgeloest = markerBlockade !== null
    && markerBlockade.schluessel === markerSchluessel
    && dataUpdatedAt > markerBlockade.seit
  const markerRefetchAusstehend = markerBlockade !== null && !blockadeAufgeloest

  // Der Zustand wird erst NACH der Aufloesung abgeraeumt (nicht in derselben
  // Ableitung), damit die Freigabe nicht bei einem spaeteren Seitenwechsel
  // wieder zur Blockade wird — aufgeloest ist aufgeloest.
  useEffect(() => {
    if (blockadeAufgeloest) setMarkerBlockade(null)
  }, [blockadeAufgeloest])

  const imAusschnitt = new Set(openReconciles.map(r => r.keywordId))
  const offeneMarkerKeywordIds = new Set<number>([...imAusschnitt, ...alleOffenenMarkerIds])
  const gesperrteKeywordIds = new Set<number>([
    ...gesperrteIds,
    // Sofort-Vereinigung fuer den ersten Frame nach einem Ladevorgang: der
    // Effekt oben greift erst danach. Die Monotonie bleibt unberuehrt — der
    // Sitzungszustand darunter schrumpft nie.
    ...offeneMarkerKeywordIds,
  ])

  // Vier Lagen, vier Saetze. Der Operator muss aus dem Titel erkennen, WARUM
  // die Zeile nicht mitgeht und ob er etwas dagegen tun kann:
  //  1. offener Marker IM Ausschnitt — quittierbar, der Ausweg steht direkt
  //     darunter.
  //  2. offener Marker JENSEITS des Ausschnitts (C3) — ebenso ungeklaert, aber
  //     „erst unten quittieren" waere hier eine Sackgasse: unten steht dieser
  //     Fall gerade nicht. Er rueckt nach, sobald die neueren erledigt sind.
  //  3. Kehrseite der Monotonie: quittiert ein ANDERER den Marker, verschwindet
  //     er aus der Antwort, die Sperre dieser Sitzung bleibt aber.
  //  4. erledigt — hier gibt es nichts zu quittieren und nichts zu klaeren, der
  //     Ausgang steht fest. Nur die Liste ist noch nicht nachgezogen.
  // Reihenfolge nicht beliebig: der ungeklaerte Ausgang ist der gefaehrliche
  // und der einzige mit Handlungsbedarf, er gewinnt.
  const blockadeGrund = (id: number): string => {
    if (imAusschnitt.has(id)) return 'Amazon-Zustand ungeklärt — erst unten quittieren, dann erneut versuchen'
    if (offeneMarkerKeywordIds.has(id)) return 'Amazon-Zustand ungeklärt — der Prüfmarker liegt außerhalb des angezeigten Ausschnitts. Er rückt nach, sobald die neueren Fälle quittiert sind.'
    if (gesperrteKeywordIds.has(id)) return 'In dieser Sitzung als ungeklärt beobachtet, der Prüfmarker steht nicht mehr in der Liste — Seite neu laden, falls der Fall inzwischen quittiert wurde.'
    return 'In dieser Sitzung bereits bei Amazon archiviert — die Kandidatenliste ist nur noch nicht nachgezogen. Ein zweiter Lauf wäre gegenstandslos.'
  }

  // Fix-Runde 3: Solange nicht feststeht, WAS gesperrt ist, startet kein Lauf.
  // Der fehlgeschlagene Abruf ist dabei der gefaehrlichste Fall — dann weiss die
  // Oberflaeche es nicht, statt es nur noch nicht zu wissen.
  //
  // Fix-Runde 4: Ein laufender HINTERGRUND-Refetch gehoert nicht dazu. Beide
  // Mengen sind monoton — wer schon gesperrt ist, bleibt es waehrend des
  // Refetchs —, ein Blockieren waere also reiner Komfortverlust ohne
  // Sicherheitsgewinn und traefe ausgerechnet den haeufigsten Ablauf: den
  // Refetch nach jedem Lauf. Fail-closed bleibt es, wo die Ansicht wirklich
  // nichts weiss: beim ersten Laden eines Schluessels (`data === undefined`,
  // z.B. nach einem Seitenwechsel) und im Fehlerzustand.
  //
  // Fix-Runde 5: Dazu kommt der `inconsistent`-Zustand — s. `markerRefetchAusstehend`.
  const sperrwissenVerwertbar = !isError && data !== undefined && !markerRefetchAusstehend
  const sperrwissenHinweis = isError
    ? 'Die Prüfmarker konnten nicht geladen werden — solange unklar ist, welche Keywords ungeklärt sind, startet kein Lauf. Bitte die Seite neu laden.'
    : markerRefetchAusstehend
      ? 'Die Rückmeldung des letzten Laufs war widersprüchlich — bis die Prüfmarker neu geladen sind, startet kein Lauf. Bis dahin steht nicht fest, welches Keyword bei Amazon angekommen ist.'
      : 'Die Prüfmarker werden geladen — bis feststeht, welche Keywords ungeklärt sind, startet kein Lauf.'

  // EIN ENGPASS, ZWEI MENGEN (Fix-Runde 2, Vorgabe des Koordinators;
  // Fix-Runde 4): Dieselbe Ableitung entscheidet (a) ob eine Zeile anklickbar
  // ist, (b) ob sie angehakt dargestellt wird, (c) was „alle auswaehlen"
  // ueberhaupt aufnehmen kann und (d) was tatsaechlich im POST landet.
  // `selected` bleibt die rohe Absicht des Operators und wird bewusst NICHT an
  // vier Stellen nachgezogen: ein Zustand, den man synchron halten muss, laeuft
  // irgendwann auseinander — eine Ableitung nicht. Eine ID, die zwischenzeitlich
  // gesperrt oder erledigt wurde, kann `selected` deshalb folgenlos enthalten.
  //
  // Die beiden Gruende bleiben getrennt (`blockadeGrund`), fuer die Frage „geht
  // das noch raus?" zaehlt aber nur ihre Vereinigung.
  const istErledigt = (id: number): boolean => erledigteIds.has(id)
  const istBlockiert = (id: number): boolean => gesperrteKeywordIds.has(id) || istErledigt(id)
  const istAngehakt = (id: number): boolean => selected.has(id) && !istBlockiert(id)
  const abzusendendeIds = [...selected].filter(id => !istBlockiert(id))

  const auswaehlbareKws = kws.filter(k => !istBlockiert(k.id))
  const alleAusgewaehlt = auswaehlbareKws.length > 0 && auswaehlbareKws.every(k => istAngehakt(k.id))

  // Die Quittier-Route liegt hinter `requireWriteMode` und antwortet im
  // Demo-Modus 423 (backend routes.ts). Also sperren statt Buttons anzubieten,
  // die garantiert fehlschlagen — dasselbe Muster wie InboxCard/TargetsList.
  // WHOLE-BRANCH-FIX C1: Die Meldungen der ungeklaerten Eintraege des letzten
  // Laufs. Die Zahl im Banner nimmt das Maximum aus dem Fehler-Envelope und
  // diesen Meldungen: beide zaehlen dieselben Eintraege, aber wenn eine der
  // beiden Quellen einmal ausfaellt, darf die Warnung nicht mit ihr ausfallen.
  const ungeklaerteMeldungen = runOutcome?.unresolvedErrors ?? []
  const ungeklaerteZahl = Math.max(reconcileCount, ungeklaerteMeldungen.length)

  const quittierGesperrt = IS_DEMO_MODE || resolveReconcile.isPending
  const quittierTitel = (fallback: string): string =>
    IS_DEMO_MODE ? 'Im Demo-Modus deaktiviert (kein Schreibzugriff)' : fallback

  function toggleSelect(id: number) {
    // Gesperrte und erledigte Keywords bleiben draussen — die Checkbox ist
    // bereits `disabled`, diese Zeile haelt die Regel unabhaengig vom DOM.
    if (istBlockiert(id)) return
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  function toggleAll() {
    if (alleAusgewaehlt) setSelected(new Set())
    else setSelected(new Set(auswaehlbareKws.map(k => k.id)))
  }

  async function handleCleanup() {
    if (absendenLaeuft.current) return
    // Kein Lauf ohne verwertbares Sperrwissen — der Button ist bereits gesperrt,
    // diese Zeile haelt die Regel unabhaengig vom DOM.
    if (!sperrwissenVerwertbar) return
    // (d) Die abgesendete Menge wird HIER, zum Absendezeitpunkt, aus denselben
    // beiden Mengen abgeleitet — statt darauf zu vertrauen, dass `selected`
    // rechtzeitig nachgezogen wurde. Ein Marker, der zwischen Auswahl und Klick
    // dazugekommen ist, faellt damit zwangslaeufig heraus; ein Keyword, das ein
    // frueherer Lauf schon archiviert hat, ebenso.
    const abzusenden = [...selected].filter(id => !istBlockiert(id))
    if (abzusenden.length === 0) return
    if (!confirm(`${abzusenden.length} Keywords archivieren? Dies kann nicht rueckgaengig gemacht werden.`)) return

    absendenLaeuft.current = true
    try {
      setReconcileCount(0)
      setReconcileUnsicher(false)
      setRunOutcome(null)
      setRunWarnung(null)
      setResolveHinweise([])
      // Die Sperrmenge wird hier BEWUSST nicht geleert — s. Deklaration.

      let ergebnis: CleanupExecuteResult
      try {
        ergebnis = await executeCleanup.mutateAsync(abzusenden)
      } catch (err) {
        // `reconcileRequiredCount` liefert nur fuer den Batch-502
        // (`ackOrReconcile`) einen Wert > 0; ein `nack(502, …)` ohne `result` und
        // jeder andere Fehler ergeben 0 und bleiben beim globalen Toast.
        setReconcileCount(reconcileRequiredCount(err))
        // Der 502 ist ein TEILergebnis. Sein Rumpf traegt den VOLLEN Batch — nur
        // zu bannern hiesse, die bereits bestaetigten Erfolge und die
        // deterministischen Fehlschlaege desselben Laufs wegzuwerfen.
        const lesung = readCleanupReconcileBody(err, abzusenden)
        if (lesung.status === 'ok') uebernehmeLaufergebnis(lesung.run)
        else if (lesung.status === 'inconsistent') verwerfeAbgesendetenBatch(abzusenden, lesung.reason)
        return
      }

      uebernehmeLaufergebnis(ergebnis)
    } finally {
      absendenLaeuft.current = false
    }
  }

  function uebernehmeLaufergebnis(ergebnis: CleanupExecuteResult) {
    const ausgang = describeCleanupRun(ergebnis)
    setRunOutcome(ausgang)

    // Ungeklaerte Ausgaenge wandern in die Sperrmenge — sie ueberdauert den
    // naechsten Lauf und faellt erst mit der Quittierung.
    if (ausgang.unresolvedKeywordIds.length > 0) {
      setGesperrteIds(prev => new Set([...prev, ...ausgang.unresolvedKeywordIds]))
    }

    // Bestaetigte Erfolge wandern in die Erledigt-Menge — sie ueberdauert den
    // Refetch, der die Kandidatenliste erst spaeter nachzieht. Gefuellt wird sie
    // aus BEIDEN Antwortformen, weil der 502-Rumpf denselben vollen Batch
    // traegt: ein bestaetigter Erfolg darin ist genauso erledigt.
    if (ausgang.archivedKeywordIds.length > 0) {
      setErledigteIds(prev => new Set([...prev, ...ausgang.archivedKeywordIds]))
    }

    // Hausmuster Scheibe F: Nach dem Lauf bleibt die Auswahl NUR fuer das, was
    // gefahrlos wiederholbar ist.
    //  - Erfolge: erledigt, sie gehoeren nicht mehr in die Arbeitsliste.
    //  - Ungeklaerte Ausgaenge: raus (und ueber die Sperre zusaetzlich gegen
    //    ein erneutes Anhaken geschuetzt).
    //  - Deterministische Fehlschlaege: bleiben stehen. Ihr Amazon-Ausgang ist
    //    bekannt und beweisbar schreiblos, die Wiederholung also gefahrlos.
    const raus = new Set(ausgang.notRepeatableKeywordIds)
    setSelected(prev => new Set([...prev].filter(id => !raus.has(id))))
  }

  function verwerfeAbgesendetenBatch(abgesendet: number[], grund: string) {
    // WICHTIG 3 (Fix-Runde 2): Widerspricht sich die Rueckmeldung, ist NICHT
    // entscheidbar, welches Keyword welchen Ausgang hatte. Dann konservativ der
    // GANZE abgesendete Batch raus aus der Auswahl — lieber eine Auswahl zu viel
    // geleert als ein ungeklaerter Write wiederholt.
    //
    // Bewusst abgewaehlt und NICHT gesperrt: eine Sperre haette hier keine
    // Gegenkraft (es gibt keinen Marker, den der Operator quittieren koennte)
    // und liesse die Zeilen bis zum Reload tot. Abwaehlen ist zurueckholbar —
    // aber nur mit einem bewussten Klick, und der Hinweis daneben sagt, warum.
    const raus = new Set(abgesendet)
    setSelected(prev => new Set([...prev].filter(id => !raus.has(id))))
    setRunWarnung(`Rückmeldung unvollständig: ${grund} Die Auswahl wurde vorsorglich geleert — vor einem neuen Lauf den Zustand bei Amazon prüfen.`)
    // Fix-Runde 3: Auch die Reconcile-Zahl stammt aus dieser Antwort. Sie taugt
    // ab hier nur noch als Untergrenze, nicht als exakter Wert.
    setReconcileUnsicher(true)
    // Fix-Runde 5, Punkt 1: Abwaehlen allein traegt seit der Refetch-Freigabe
    // nicht mehr — der Batch waere sofort wieder anwaehlbar. Bis die Marker neu
    // geladen sind, startet hier kein Lauf.
    //
    // Whole-Branch-Fix C2: Der Stand wird HIER aus dem Cache gelesen und nicht
    // aus dem Render-Abschluss uebernommen. Ein zwischenzeitlich
    // durchgelaufener Hintergrund-Refetch haette den Wert im Abschluss veraltet
    // gelassen — die Blockade waere dann sofort wieder gefallen, obwohl der
    // Abruf, der die Wahrheit bringen soll, noch aussteht.
    const standJetzt = queryClient.getQueryState(cleanupKeywordsQueryKey(abfrage))?.dataUpdatedAt ?? 0
    setMarkerBlockade({ schluessel: markerSchluessel, seit: standJetzt })
  }

  function hebeSperreAuf(keywordId: number) {
    setGesperrteIds(prev => {
      if (!prev.has(keywordId)) return prev
      const next = new Set(prev)
      next.delete(keywordId)
      return next
    })
  }

  async function handleResolve(marker: CleanupOpenReconcile, outcome: CleanupReconcileOutcome) {
    // Koordinator-Auflage 2026-08-04: der Vorbehalt gehoert in den Moment der
    // Handlung. Der Dialog darf keine Pruefung durch das System suggerieren.
    const frage = outcome === 'archived'
      ? 'Bei Amazon nachgesehen: Das Keyword ist dort archiviert?\n\nDiese Angabe wird als manuelle Feststellung gespeichert — es findet kein Abgleich mit Amazon statt. Der lokale Stand wird entsprechend nachgezogen.'
      : 'Bei Amazon nachgesehen: Das Keyword ist dort NICHT archiviert?\n\nDiese Angabe wird als manuelle Feststellung gespeichert — es findet kein Abgleich mit Amazon statt. Der Prüfmarker wird geschlossen, das Keyword bleibt unverändert.'
    if (!confirm(frage)) return
    setResolveHinweise([])

    try {
      const ergebnis = await resolveReconcile.mutateAsync({ id: marker.id, outcome })
      // Die Sperre dieses Keywords faellt mit der bestaetigten Quittierung —
      // das ist das einzige Ereignis, das die monotone Menge schrumpfen darf.
      // Der DURABLE Teil bleibt unberuehrt: ein noch offener Geschwister-Marker
      // haelt die Zeile ueber `openReconciles` weiter gesperrt.
      hebeSperreAuf(marker.keywordId)
      // Fix-Runde 5, Punkt 2: NUR bei `archived`. Der Operator hat gerade
      // festgestellt, dass das Keyword bei Amazon archiviert IST — ein weiterer
      // Lauf darauf waere gegenstandslos. Gefaehrlich ist er nicht (die
      // Quittierung setzt lokal `state='archived'`, danach verwirft
      // `executeCleanup` das nicht mehr enabled-e Keyword vor jedem
      // Amazon-Call), aber ein Lauf ohne Wirkung gehoert nicht angeboten.
      // Bei `not_archived` bleibt die Zeile anklickbar: dort liegt bei Amazon
      // nichts, die Wiederholung ist genau die richtige Handlung.
      if (outcome === 'archived') setErledigteIds(prev => new Set([...prev, marker.keywordId]))

      // WHOLE-BRANCH-FIX I1: `keywordUpdated` hatte bis hierhin GAR KEINEN
      // Konsumenten. Die Ansicht buchte das Keyword als erledigt, egal ob die
      // lokale Buchung gegriffen hatte — der Operator erfuhr nichts davon. Der
      // gefaehrliche Fall (lokal ein anderer Zustand) kommt seit dem
      // Backend-Fix gar nicht mehr hier an, sondern als 409 unten. Die
      // harmlosen Faelle bleiben — und auch die gehoeren gesagt, sonst haengt
      // die Sichtbarkeit wieder an einem Feld, das niemand liest.
      const hinweise: string[] = []
      if (outcome === 'archived' && !ergebnis.keywordUpdated) {
        hinweise.push(ergebnis.localBookingNote
          ? `Der lokale Stand wurde nicht nachgezogen — ${ergebnis.localBookingNote}`
          : 'Der lokale Stand wurde nicht nachgezogen. Die Quittierung gilt trotzdem; bitte den lokalen Zustand des Keywords pruefen.')
      }
      // Ohne diesen Zweig stuende der Hinweis auf die ausgefallene Audit-Spur
      // nur in der Antwort — und damit nicht vor den Augen des Operators.
      if (ergebnis.auditNote) hinweise.push(ergebnis.auditNote)
      if (hinweise.length > 0) setResolveHinweise(hinweise)
    } catch (err) {
      // 404 = jemand anderes war schneller. Normalfall, kein Zwischenfall: die
      // Liste ist bereits neu geladen (Hook), hier bleibt die sachliche Meldung.
      if (err instanceof ApiError && err.status === 404) {
        // Fix-Runde 3: Der 404 IST ein bestaetigtes Ereignis — „dieser Marker
        // ist erledigt", nur eben von jemand anderem. Anders als ein blosser
        // Refetch darf er die Sperre aufheben. Ohne diesen Zweig bliebe das
        // Keyword bis zum Reload gesperrt, OHNE dass es noch einen
        // quittierbaren Marker gaebe — eine Sackgasse.
        hebeSperreAuf(marker.keywordId)
        // FINDING 4 (Fix-Runde 1): Praesens, nicht Perfekt. Die Invalidierung
        // ist zu diesem Zeitpunkt ausgeloest (der `onError` des Hooks laeuft vor
        // dem Ablehnen von `mutateAsync`), aber NICHT abgewartet. „wurde neu
        // geladen" behauptete einen abgeschlossenen Refetch — in einem Modul,
        // dessen Zweck zutreffende Meldungen sind, faellt das doppelt auf.
        setResolveHinweise(['Dieser Prüfmarker war bereits quittiert — die Liste wird neu geladen.'])
        return
      }
      // WHOLE-BRANCH-FIX I1: 409 = der lokale Zustand widerspricht der
      // Feststellung. Die Quittierung hat NICHT stattgefunden, der Marker ist
      // unveraendert offen — also nichts freigeben, nichts als erledigt buchen,
      // nur die Begruendung des Backends bleibend anzeigen. Ein Toast waere
      // nach Sekunden weg, waehrend der Widerspruch bleibt.
      if (err instanceof ApiError && err.status === 409) {
        setResolveHinweise([err.message])
        return
      }
      // Alles andere (423 Demo-Modus, 403, 5xx) meldet der globale Toast.
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-[var(--muted-foreground)]">
            Keywords mit 0 Impressions seit mehr als 30 Tagen.
          </p>
          <p className="text-sm font-medium">{total} betroffene Keywords</p>
        </div>
        {/* Zahl und Sichtbarkeit aus DERSELBEN Ableitung wie der Request — sonst
            stuende hier eine Zahl, die der POST nicht absendet. */}
        {abzusendendeIds.length > 0 && (
          <button onClick={handleCleanup} disabled={executeCleanup.isPending || !sperrwissenVerwertbar}
            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50">
            <Trash2 className="h-3.5 w-3.5" />
            {executeCleanup.isPending ? 'Archiviere...' : `${abzusendendeIds.length} archivieren`}
          </button>
        )}
      </div>

      {/* Fix-Runde 3: Der Button ist gesperrt, solange das Sperrwissen nicht
          steht — kommentarlos wäre das für den Operator nicht erklärbar. */}
      {!sperrwissenVerwertbar && (isError || abzusendendeIds.length > 0) && (
        <div role="status" className="rounded-md border border-[var(--border)] bg-[var(--muted)] px-3 py-2 text-xs text-[var(--muted-foreground)]">
          {sperrwissenHinweis}
        </div>
      )}

      {/* CLEANUP-SICHT: der FLUECHTIGE Weg — was der gerade gelaufene Batch
          offen gelassen hat. Statt Toast (Muster KeywordList/CampaignList): der
          verschwindet nach Sekunden, waehrend die betroffene Auswahl steht. */}
      {ungeklaerteZahl > 0 && (
        <div role="alert" className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            {/* Fix-Runde 3: Nach einer als unzuverlässig erkannten Antwort ist
                diese Zahl nur noch eine Untergrenze — sie stammt aus demselben
                Rumpf, dem gerade widersprochen wurde. */}
            <p className="text-sm font-medium">
              {reconcileUnsicher
                ? `Mindestens ${ungeklaerteZahl} ${ungeklaerteZahl === 1 ? 'Eintrag' : 'Einträge'} prüfbedürftig — die Rückmeldung war unvollständig, deshalb den gesamten Lauf bei Amazon prüfen.`
                : `${ungeklaerteZahl} ${ungeklaerteZahl === 1 ? 'Eintrag' : 'Einträge'} prüfbedürftig — Amazon-Zustand ungeklärt.`}
            </p>
            <p className="text-xs text-red-700">
              Der Archivierungs-Auftrag kann bei Amazon angekommen sein. Nicht erneut ausführen —
              zuerst bei Amazon nachsehen und unten quittieren.
            </p>
            {/* WHOLE-BRANCH-FIX C1: die MELDUNGEN der ungeklärten Einträge, im
                Wortlaut des Backends. Hier hängt der Hinweis dran, dass der
                durable Prüfmarker NICHT geschrieben werden konnte — dann gibt
                es unten nichts zu quittieren, und nach einem Reload ist weder
                Marker noch Sperre da. Diese Sätze verschwanden bisher an der
                Schichtgrenze; ohne sie fordert das Banner darüber zu etwas auf,
                das gar nicht möglich ist. */}
            {ungeklaerteMeldungen.length > 0 && (
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-red-700">
                {ungeklaerteMeldungen.map((meldung, i) => <li key={i}>{meldung}</li>)}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Die BEKANNTEN Ausgaenge desselben Laufs — bestaetigte Erfolge und
          deterministische Fehlschlaege. Bewusst KEIN Alarm-Ton und
          ausdruecklich nicht die Anweisung "nicht erneut ausführen": hier steht
          der Amazon-Zustand fest. Erscheint bei 200 UND bei 502, weil der
          502-Rumpf denselben vollen Batch traegt. */}
      {runOutcome && (
        <div role="status"
          className={`rounded-md border px-3 py-2 text-xs ${runOutcome.deterministicErrors.length > 0
            ? 'border-amber-200 bg-amber-50 text-amber-800'
            : 'border-[var(--border)] bg-[var(--muted)] text-[var(--muted-foreground)]'}`}>
          <p className="font-medium">
            Ergebnis des Laufs: {runOutcome.archived} {runOutcome.archived === 1 ? 'Keyword' : 'Keywords'} bei Amazon archiviert.
          </p>
          {runOutcome.deterministicErrors.length > 0 && (
            <>
              <p className="mt-1">
                {runOutcome.deterministicErrors.length} {runOutcome.deterministicErrors.length === 1 ? 'Fehlschlag' : 'Fehlschläge'} mit
                bekanntem Amazon-Zustand — gefahrlos wiederholbar, die Auswahl bleibt dafür stehen.
              </p>
              <ul className="mt-1 list-disc space-y-0.5 pl-4">
                {runOutcome.deterministicErrors.map((fehler, i) => <li key={i}>{fehler}</li>)}
              </ul>
            </>
          )}
        </div>
      )}

      {/* Fix-Runde 2, WICHTIG 3: Der Lauf hat sich widersprochen — dann gibt es
          keine belastbare Erfolgsauskunft, und die Auswahl ist vorsorglich
          geleert. Der Operator muss erfahren, warum sie plötzlich leer ist. */}
      {runWarnung && (
        <div role="status" className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {runWarnung}
        </div>
      )}

      {resolveHinweise.length > 0 && (
        <div role="status" className="space-y-1 rounded-md border border-[var(--border)] bg-[var(--muted)] px-3 py-2 text-xs text-[var(--muted-foreground)]">
          {resolveHinweise.map((hinweis, i) => <p key={i}>{hinweis}</p>)}
        </div>
      )}

      {/* CLEANUP-SICHT: der DURABLE Weg — der Bestand offener Pruefmarker aus
          `cleanup_events`. Ueberlebt Reload und Sessionwechsel und ersetzt das
          Banner darueber nicht: das eine meldet einen Lauf, das andere den
          Rueckstand. */}
      {openReconciles.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
          {/* WHOLE-BRANCH-FIX C3: Die Zahl ist der BESTAND, nicht die Länge der
              Liste darunter. Bisher stand hier die Zeilenzahl — bei mehr als
              100 offenen Fällen behauptete die Überschrift damit einen
              Rückstand, der kleiner war als der wirkliche. */}
          <p className="text-sm font-semibold text-amber-900">
            Cleanup — prüfbedürftig ({offeneMarkerGesamt})
          </p>
          {offeneMarkerGesamt > openReconciles.length && (
            <p className="mt-1 text-xs text-amber-800">
              Angezeigt werden die neuesten {openReconciles.length} von {offeneMarkerGesamt} offenen Fällen —
              die übrigen {offeneMarkerGesamt - openReconciles.length} bleiben gesperrt und rücken nach,
              sobald die hier gelisteten quittiert sind.
            </p>
          )}
          {/* Koordinator-Auflage 2026-08-04: Die Quittierung ist eine
              Feststellung des Operators, keine Systemprüfung — das muss im
              Moment der Handlung dastehen, nicht nur im Wiki. */}
          <p className="mt-1 text-xs text-amber-800">
            Zustand bei Amazon selbst nachsehen. Die Quittierung hält nur fest, was dabei
            festgestellt wurde — sie ruft Amazon nicht ab und prüft nichts nach.
          </p>
          {IS_DEMO_MODE && (
            <p className="mt-1 text-xs text-amber-800">
              <strong className="font-bold">Demo-Modus:</strong> Quittieren ist deaktiviert —
              das Backend blockt jeden Schreibpfad (423). Nur Ansicht.
            </p>
          )}
          <ul className="mt-2 space-y-2">
            {openReconciles.map(r => (
              <li key={r.id} className="flex items-start justify-between gap-3 text-xs">
                <span>
                  <span className="font-mono">{r.keywordText ?? `Keyword ${r.keywordId}`}</span>
                  <span className="block text-amber-800">{r.reason}</span>
                  <span className="block text-amber-700">
                    {new Date(r.createdAt).toLocaleString('de-DE')}
                  </span>
                </span>
                <span className="flex shrink-0 gap-1">
                  <button onClick={() => handleResolve(r, 'archived')}
                    disabled={quittierGesperrt}
                    title={quittierTitel('Als bei Amazon archiviert festhalten')}
                    className="rounded-md border border-amber-400 px-2 py-1 disabled:opacity-50">
                    war archiviert
                  </button>
                  <button onClick={() => handleResolve(r, 'not_archived')}
                    disabled={quittierGesperrt}
                    title={quittierTitel('Als bei Amazon NICHT archiviert festhalten')}
                    className="rounded-md border border-amber-400 px-2 py-1 disabled:opacity-50">
                    war NICHT archiviert
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-[var(--border)]">
        <table className="min-w-full text-sm">
          <thead className="border-b border-[var(--border)] bg-[var(--muted)]">
            <tr>
              <th className="px-3 py-2 text-left">
                <input type="checkbox" checked={alleAusgewaehlt}
                  onChange={toggleAll} className="rounded" />
              </th>
              <th className="px-3 py-2 text-left font-medium">Keyword</th>
              <th className="px-3 py-2 text-left font-medium">Match Type</th>
              <th className="px-3 py-2 text-right font-medium">Alter (Tage)</th>
              <th className="px-3 py-2 text-left font-medium">Erstellt</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-[var(--muted-foreground)]">Lade...</td></tr>
            )}
            {!isLoading && kws.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-[var(--muted-foreground)]">Keine Keywords gefunden — alles sauber!</td></tr>
            )}
            {kws.map(kw => (
              <tr key={kw.id} className="border-b border-[var(--border)] hover:bg-[var(--muted)]/50">
                <td className="px-3 py-2">
                  <input type="checkbox" checked={istAngehakt(kw.id)}
                    disabled={istBlockiert(kw.id)}
                    title={istBlockiert(kw.id) ? blockadeGrund(kw.id) : undefined}
                    onChange={() => toggleSelect(kw.id)} className="rounded disabled:opacity-40" />
                </td>
                <td className="px-3 py-2 font-mono text-xs">{kw.text}</td>
                <td className="px-3 py-2 text-xs capitalize">{kw.matchType}</td>
                <td className="px-3 py-2 text-right tabular-nums">{kw.daysSinceCreation}</td>
                <td className="px-3 py-2 text-xs text-[var(--muted-foreground)]">
                  {new Date(kw.createdAt).toLocaleDateString('de-DE')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-[var(--muted-foreground)]">
          <span>Seite {page + 1} von {totalPages}</span>
          <div className="flex gap-1">
            <button onClick={() => setOffset(Math.max(0, offset - limit))} disabled={page === 0}
              className="rounded-md border border-[var(--border)] p-1.5 disabled:opacity-30">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button onClick={() => setOffset(offset + limit)} disabled={page >= totalPages - 1}
              className="rounded-md border border-[var(--border)] p-1.5 disabled:opacity-30">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
